import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, ShieldCheck, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"

type RoleTypeFilter = 'all' | 'user' | 'workload'

const ROLE_TYPE_BADGE: Record<string, string> = {
  user: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  workload: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
}

export function RoleList() {
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [roleTypeFilter, setRoleTypeFilter] = useState<RoleTypeFilter>('all')
  const [orgFilter, setOrgFilter] = useState("")
  const itemsPerPage = 10

  const { data, isLoading, isError, refetch } = useApiQuery("getRoles", {})

  const roles = ((data as any)?.content?.roles || (data as any)?.roles || []).map((r: any) => ({
    id: r.id || String(r.roleNumber),
    roleNumber: r.roleNumber,
    roleName: r.roleName,
    roleDescription: r.roleDescription ?? '',
    roleType: r.roleType,
    namespace: r.namespace,
    organization: r.organization,
    roleUri: r.roleUri ?? '',
  }))

  const filteredRoles = roles.filter((r: any) => {
    const matchesSearch = r.roleName?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType = roleTypeFilter === 'all' || r.roleType === roleTypeFilter
    const matchesOrg = !orgFilter || r.organization?.toLowerCase().includes(orgFilter.toLowerCase())
    return matchesSearch && matchesType && matchesOrg
  })

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedRoles = filteredRoles.slice(startIndex, startIndex + itemsPerPage)

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load roles</p>
          <Button variant="outline" onClick={() => refetch()}>Try Again</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Manage Roles</CardTitle>
                <CardDescription className="text-base">Define RBAC roles for users and workloads</CardDescription>
              </div>
            </div>
            <Link to="/rema/roles/add">
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Role
              </Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {(['all', 'user', 'workload'] as RoleTypeFilter[]).map((type) => (
                <Button
                  key={type}
                  size="sm"
                  variant={roleTypeFilter === type ? 'default' : 'outline'}
                  onClick={() => { setRoleTypeFilter(type); setCurrentPage(1) }}
                >
                  {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
                </Button>
              ))}
              {roleTypeFilter === 'user' && (
                <Input
                  placeholder="Filter by organization..."
                  value={orgFilter}
                  onChange={(e) => { setOrgFilter(e.target.value); setCurrentPage(1) }}
                  className="max-w-xs"
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="w-[80px] font-semibold text-base">ID</TableHead>
                <TableHead className="font-semibold text-base">Name</TableHead>
                <TableHead className="font-semibold text-base">Description</TableHead>
                <TableHead className="w-[110px] font-semibold text-base">Type</TableHead>
                <TableHead className="font-semibold text-base">URI</TableHead>
                <TableHead className="w-[90px] text-right font-semibold text-base">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRoles.length > 0 ? (
                paginatedRoles.map((role: any) => (
                  <TableRow key={role.roleNumber} className="transition-colors hover:bg-muted/50">
                    <TableCell className="font-medium text-base">{role.roleNumber}</TableCell>
                    <TableCell className="font-medium text-base">
                      <Link
                        to="/rema/roles/$roleNumber"
                        params={{ roleNumber: String(role.roleNumber) }}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {role.roleName}
                      </Link>
                    </TableCell>
                    <TableCell className="text-base text-muted-foreground">{role.roleDescription || '—'}</TableCell>
                    <TableCell>
                      <Badge className={ROLE_TYPE_BADGE[role.roleType] ?? ''}>{role.roleType}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <span className="text-xs font-mono truncate block" title={role.roleUri}>{role.roleUri || '—'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                        <Link to="/rema/roles/$roleNumber/edit" params={{ roleNumber: String(role.roleNumber) }}>
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit {role.roleName}</span>
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No roles found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-base text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredRoles.length)} of {filteredRoles.length} roles
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
